import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getExam } from '../../services/examService';
import { getPatient } from '../../services/patientService';
import { getCase } from '../../services/caseService';
import EXAM_SECTIONS from '../../constants/examConfig';

const ExamOverview = () => {
  const { patientId, caseId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [examData, setExamData] = useState({});
  const [patient, setPatient] = useState(null);
  const [treatmentCase, setTreatmentCase] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [patientId, caseId, userProfile]);

  const loadData = async () => {
    if (!userProfile) return;
    try {
      setLoading(true);
      const [exam, pat, cas] = await Promise.all([
        getExam(userProfile.practiceId, patientId, caseId),
        getPatient(userProfile.practiceId, userProfile.id, patientId),
        getCase(userProfile.practiceId, userProfile.id, patientId, caseId)
      ]);
      setExamData(exam || {});
      setPatient(pat);
      setTreatmentCase(cas);
    } catch (err) {
      console.error('Error loading exam:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderIndicator = (item) => {
    if (item.icon === 'check') {
      const color = item.color || 'orange';
      const colors = {
        orange: 'text-orange-500',
        red: 'text-red-500',
        green: 'text-green-500',
        yellow: 'text-yellow-500',
        blue: 'text-blue-500'
      };
      return <span className={colors[color] || colors.orange}>&#10004;</span>;
    }
    if (item.value) {
      const dotColors = {
        red: 'bg-red-500',
        yellow: 'bg-yellow-400',
        green: 'bg-emerald-500',
        blue: 'bg-blue-500'
      };
      return (
        <span className="inline-flex items-center gap-1.5">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColors[item.color] || dotColors.yellow}`}></span>
          <span className="font-semibold text-gray-800">{item.value}</span>
        </span>
      );
    }
    if (item.color) {
      const dotColors = {
        red: 'bg-red-500',
        yellow: 'bg-yellow-400',
        green: 'bg-emerald-500',
        blue: 'bg-blue-500'
      };
      return <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColors[item.color]}`}></span>;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4 sm:space-y-6">
      <div>
        <Link
          to={`/patients/${patientId}/cases/${caseId}`}
          className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
        >
          &larr; Back to case
        </Link>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Clinical Exam</h1>
            {patient && treatmentCase && (
              <p className="text-sm text-gray-500 mt-1">
                {patient.firstName} {patient.lastName} &mdash; {treatmentCase.name}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate(`/patients/${patientId}/cases/${caseId}/exam/1`)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition self-start"
          >
            {Object.keys(examData).length > 0 ? 'Continue Exam' : 'Start Exam'}
            <svg className="w-4 h-4 ml-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Section Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {EXAM_SECTIONS.map((section) => {
          const sectionData = examData[section.id];
          const summary = section.getSummary(sectionData || {});
          const hasData = sectionData && Object.keys(sectionData).length > 0;

          return (
            <button
              key={section.id}
              onClick={() => navigate(`/patients/${patientId}/cases/${caseId}/exam/${section.number}`)}
              className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 text-left hover:shadow-md hover:border-blue-200 transition group relative"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-sm font-bold text-gray-800 leading-tight pr-4">
                  {section.id === 'history' ? 'History' :
                   section.id === 'cancer' ? 'Cancer' :
                   section.id === 'tmjQuestions' ? 'TMJ' :
                   section.name}
                </h3>
                <svg className="w-4 h-4 text-gray-300 group-hover:text-blue-400 flex-shrink-0 mt-0.5 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>

              {summary.length > 0 ? (
                <div className="space-y-1.5">
                  {summary.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="truncate">{item.label}:</span>
                      {renderIndicator(item)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 italic">
                  {hasData ? 'In progress' : 'Not started'}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ExamOverview;
